import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import type { TransferStatus } from './transferStatus';

/**
 * Maps a real outbox item to the status the display machine should aim for. `nowMs` is injected
 * rather than read internally, which is what makes this function testable without a simulated
 * clock. The `'pending'` case is the only one that reads more than `item.state`: no network, a
 * scheduled retry, and a prior attempt all mean the item is no longer a plain queue entry, and no
 * network is kept distinct from the other two because it reads differently to a user who has signal.
 */
const resolveTransferStatus = (
	item: OutboxItem,
	context: { readonly isOnline: boolean; readonly nowMs: number },
): TransferStatus => {
	switch (item.state) {
		case 'draft':
			return 'preparing';
		case 'pending':
			if (!context.isOnline) {
				return 'offline';
			}
			if (item.nextAttemptAt !== null && item.nextAttemptAt > context.nowMs) {
				return 'waiting';
			}
			if (item.attempts > 0) {
				return 'waiting';
			}
			return 'preparing';
		case 'transferring':
			return 'transferring';
		case 'verifying':
			return 'verifying';
		case 'confirmed':
			return 'confirmed';
		case 'failed':
		case 'orphaned':
			// An orphaned item never reaches the transfer card: the upload page shows its banner first.
			return 'failed';
		case 'expired':
			return 'expired';
	}
};

export default resolveTransferStatus;
